/*************************************************************************
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: Adobe permits you to use, modify, and distribute this file in
 * accordance with the terms of the Adobe license agreement accompanying
 * it. If you have received this file from a source other than Adobe,
 * then your use, modification, or distribution of it requires the prior
 * written permission of Adobe.
 **************************************************************************/

/**
 * APIs for managing a client side persisted cache.
 * Unified Shell will store the data against its own domain, which allows data caching
 * even when the actual app runs on a domain which is 3rd party to Unified Shell's.
 *
 * For example, the API will work in Chrome incognito when Unified Shell runs on an adobe.com domain
 * and the embedded applications run on the adobe.net domain.
 *
 * These APIs utilizes the [Browser Cache API](https://developer.mozilla.org/en-US/docs/Web/API/Cache) under the hood and are all run asynchronously.
 *
 * If migrating from Local Storage to Cache API, make sure all the app logic is async first.
 *
 * NOTE: This API should not be used to cache any PII data to comply with Adobe privacy policies.
 *
 * To consume this API, add the following import to your code.
 *
 * ```typescript
 * import cache from '@adobe/exc-app/cache';
 * ```
 *
 * The default export is an object of type [CacheAPI](../interfaces/_cache_.cacheapi.md)
 *
 * API reference: [scroll down](#index)
 *
 * ### Sample code
 *
 * ```typescript
 * import cache, {CacheTTL, CacheScope} from '@adobe/exc-app/cache';
 *
 * // By default, cached data is tied to the user and cached for a week.
 * await cache.set<MyType>({key: 'some-cache-key', value: myTypeInstance});
 *
 * // Both scope and expiry can be adjusted.
 * await cache.set<MyType>({key: 'some-cache-key', value: myTypeInstance, expiry: CacheTTL.DAY, scope: CacheScope.ORG});
 *
 * // It is also possible to bind cache data to the current IMS session, it will expire when the user logs out.
 * await cache.set<MyType>({key: 'some-cache-key', value: myTypeInstance, expiry: CacheTTL.IMS_SESSION});
 *
 * // Cache get
 * const myData: MyType = await cache.get<MyType>({key: 'some-cache-key'});
 *
 * // Get scope must be identical to the scope used for set - Always use the same scope for both.
 * const myData2: MyType = await cache.get<MyType>({key: 'some-cache-key', scope: CacheScope.ORG});
 *
 * // Cache delete.
 * await cache.delete({key: 'some-cache-key'});
 * ```
 * @packageDocumentation
 * @module cache
 */

import {getImpl} from './src/Global';

/**
 * @ignore
 */
export enum CacheScope {
  /**
   * Default - Same as USER
   */
  DEFAULT = 'user',
  /**
   * Cache data at the IMS Org level.
   */
  ORG = 'org',
  /**
   * Cache data at the AEP Sandbox level.
   */
  SANDBOX = 'sandbox',
  /**
   * Cache data at the Sub Org level.
   */
  SUBORG = 'suborg',
  /**
   * Cache data at the user level.
   */
  USER = 'user'
}

/**
 * @ignore
 */
export enum CacheTTL {
  /**
   * Disable caching for this value.
   */
  DONT_CACHE = 0,
  /**
   * Cache data for the duration of the current IMS session only
   * Data will not be available once the user has logged in again.
   */
  IMS_SESSION = -1,
  /**
   * Default - One week
   */
  DEFAULT = 3600 * 24 * 7,
  /**
   * Cache data for an hour.
   */
  HOUR = 3600,
  /**
   * Cache data for a day.
   */
  DAY = 3600 * 24,
  /**
   * Cache data for a week.
   */
  WEEK = 3600 * 24 * 7,
  /**
   * Cache data for a month (30 days actual).
   */
  MONTH = 3600 * 24 * 30
}

export type CacheExpiry = CacheTTL | number;

/**
 * Input parameters for the Cache get and delete APIs.
 */
export interface CacheParameters {
  /**
   * Cache key.
   */
  key: string;
  /**
   * Cache scope (Optional - Defaults to user).
   */
  scope?: CacheScope;
  /**
   * Shared Cache name.
   * Calling app must be allowlisted by Unified Shell to access this cache.
   */
  sharedCache?: string;
}

/**
 * Input parameters for the Cache set API.
 */
export interface CacheSetParameters<T> extends CacheParameters {
  /**
   * Cache expiry. Can be one of the set values defined by CacheTTL, or a numeric cache ttl (in seconds).
   * Optional - Defaults to one week.
   */
  expiry?: CacheExpiry;
  /**
   * Data to cache. Data must be serializable (JSON).
   */
  value: T;
}

/**
 * Cache entry (Returned by the get API).
 */
export type CacheEntry<T> = {
  createdAt: number;
  key: string;
  value: T;
} & ({expiresAt: number} | {expireBySession: true});

/**
 * APIs for managing a client side persisted cache.
 */
export interface CacheApi {
  /**
   * Deletes a value from the cache (If available).
   * @param params Parameters used to identify the cached item to delete.
   */
  delete(params: CacheParameters): Promise<void>;
  /**
   * Gets a value from the cache if one is available.
   * @param params Parameters used to identify the cached item to retrieve.
   * @returns A promise that resolves to the specified value, or undefined if none exist.
   */
  get<T>(params: CacheParameters): Promise<CacheEntry<T> | undefined>;

  /**
   * Stores a value in the cache.
   * @param params Parameters used to identify the cached item to store and control its TTL.
   */
  set<T>(params: CacheSetParameters<T>): Promise<void>;
}

const cache: CacheApi = {
  delete: params => getImpl('cache')().delete(params),
  get: params => getImpl('cache')().get(params),
  set: params => getImpl('cache')().set(params)
};

export default cache;
